"use client";

import { Fragment, ReactNode, RefObject } from "react";

import { CloseIcon } from "@saas/shared/icon";
import { classNames } from "@saas/shared/utils";

import { BaseButton } from "../base-button/base-button";

import { Dialog, Transition } from "@headlessui/react";

const Size = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
  XLARGE: "x-large",
} as const;

const sizeClass = {
  [Size.SMALL]: "md:max-w-[360px]",
  [Size.MEDIUM]: "md:max-w-[552px]",
  [Size.LARGE]: "md:max-w-[744px]",
  [Size.XLARGE]: "md:max-w-[936px]",
};

export interface BaseDrawerModalProps {
  /**
   * Whether the modal is open or not
   */
  open: boolean;
  /**
   * The title of the modal
   */
  title: string;
  /**
   * The width size of the modal for desktop
   * - small: 360px
   * - medium: 552px
   * - large: 744px
   * - x-large: 936px
   */
  size: (typeof Size)[keyof typeof Size];
  /**
   * The ref of the element that should be focused when the modal opens
   */
  initialRef: RefObject<HTMLElement>;
  /**
   * The testid of the modal
   */
  testid?: string;
  /**
   * The content of the modal
   */
  children: ReactNode;
  /**
   * The callback when the modal is closed
   */
  onClose?: (open: boolean) => void;
  /**
   * The callback to override the default close action
   */
  onReset?: () => void;
  /**
   * Whether the title is shown for desktop
   */
  showTitle?: boolean;
  /**
   * Options to override other dialog or modal
   */
  priorityIndex?: number;
  className?: string;
  showCloseButton?: boolean;
  titleClassName?: string;
}

/**
 *
 * Responsive modal that use drawer pattern for mobile.
 * Content is scrollable.
 */
export const BaseDrawerModal = ({
  open,
  title,
  size,
  initialRef,
  testid = "",
  children,
  className,
  titleClassName,
  priorityIndex = 10,
  showTitle = true,
  showCloseButton = true,
  onClose,
  onReset,
}: BaseDrawerModalProps) => {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        initialFocus={initialRef}
        className={classNames("fixed inset-0", `z-${priorityIndex}`)}
        onClose={onClose ?? (() => void {})}
      >
        <div className={"min-h-dvh flex"}>
          <Transition.Child
            as={Fragment}
            enter={"ease-out duration-300"}
            enterFrom={"opacity-0"}
            enterTo={"opacity-100"}
            leave={"ease-in duration-200"}
            leaveFrom={"opacity-100"}
            leaveTo={"opacity-0"}
          >
            <Dialog.Overlay
              className={"bg-neutral-N700/40 fixed inset-0 transition-opacity"}
            />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter={"ease-out duration-300"}
            enterFrom={"opacity-0 translate-y-4 md:translate-y-0 md:scale-95"}
            enterTo={"opacity-100 translate-y-0 md:scale-100"}
            leave={"ease-in duration-200"}
            leaveFrom={"opacity-100 translate-y-0 md:scale-100"}
            leaveTo={"opacity-0 translate-y-4 md:translate-y-0 md:scale-95"}
          >
            <div
              className={classNames(
                "md:shadow-elevation-1 shadow-elevation-2 bg-neutral-N0 z-[1] mx-auto mt-16 max-h-[calc(100dvh-4rem)] w-full self-end rounded-t-3xl p-6 md:my-auto md:rounded-lg",
                sizeClass[size]
              )}
              data-testid={testid}
            >
              <span
                className={classNames(
                  "absolute inset-x-0 -top-3 z-10 md:hidden",
                  !onClose && "hidden"
                )}
              >
                <hr
                  className={
                    "bg-neutral-N0 shadow-elevation-1 mx-auto h-1 w-14 rounded-3xl border-0 md:hidden"
                  }
                />
              </span>

              <div
                className={classNames(
                  showTitle && "mb-4",
                  "flex",
                  onReset
                    ? "flex-row items-center justify-between"
                    : "flex-row-reverse items-center justify-end",
                  "gap-4 md:flex-row md:items-start md:justify-between"
                )}
              >
                <Dialog.Title
                  className={classNames(
                    "title-large line-clamp-2 md:line-clamp-none",
                    !showTitle && "md:sr-only",
                    titleClassName
                  )}
                  data-testid={testid + "__title"}
                >
                  {title}
                </Dialog.Title>

                <DrawerHeaderActions
                  testid={testid}
                  showCloseButton={showCloseButton}
                  onReset={onReset}
                  onClose={onClose}
                />
              </div>

              <div
                className={classNames(
                  "no-scrollbar h-auto max-h-[calc(100dvh-10rem)] overflow-auto md:max-h-[calc(70dvh-4rem)] md:pb-0",
                  className
                )}
              >
                {children}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

type DrawerHeaderActionsProps = Pick<
  BaseDrawerModalProps,
  "onClose" | "showCloseButton" | "testid" | "onReset"
>;
const DrawerHeaderActions = ({
  showCloseButton,
  testid,
  onReset,
  onClose,
}: DrawerHeaderActionsProps) => {
  if (onReset) {
    return (
      <BaseButton
        className={"p-0"}
        variant={"text"}
        onClick={onReset}
        data-testid={testid + "__reset"}
      >
        Reset
      </BaseButton>
    );
  }

  if (showCloseButton && onClose) {
    return (
      <button
        className={"group flex-shrink-0"}
        onClick={() => onClose(false)}
        data-testid={testid + "__close"}
      >
        <span className={"sr-only"}>Close</span>
        <CloseIcon
          className={
            "text-neutral-N600 group-hover:text-neutral-N600/80 h-6 w-6"
          }
        />
      </button>
    );
  }

  return null;
};

export default BaseDrawerModal;
